import { Flex, RpiButton, RpiText } from "@rpi/openapi-core";
import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { AppPage } from "../../../components/AppPage";
import { PaymentMethod } from "../../../components/PaymentMethod";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { RpiNotification, useRpiNotifications } from "../../../hooks/useRpiNotifications";
import { PaymentMethodCreateView } from "./PaymentMethodCreateViewV2";

interface PaymentInformationSectionProps {
  refetchDep: boolean;
}

export const PaymentInformationSection: React.FC<PaymentInformationSectionProps> = ({ refetchDep }) => {
  const user = useAuthUser();
  const notifications = useRpiNotifications();
  const [addPaymentDrawerOpen, setAddPaymentDrawerOpen] = React.useState(false);

  const { data, isLoading, error, refetch } = GraphQL.useListPaymentMethodsQuery(
    { customerPublicId: user.id! },
    { enabled: !!user.id }
  );
  const paymentMethods = data?.listPaymentMethods
    ? [...data.listPaymentMethods].sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
    : undefined;

  React.useEffect(() => {
    if (refetchDep) {
      void refetch();
    }
  }, [refetchDep, refetch]);

  const hasPaymentMethods = Boolean(paymentMethods && paymentMethods.length > 0);

  return (
    <AppPage.Section mt={32} title="Payment information" size="small" error={error} isLoading={isLoading}>
      <Flex.Column mt={8} gap={8} fullWidth sx={(theme) => ({ color: theme.colors.brand[5] })}>
        {!hasPaymentMethods && (
          <RpiText type="p3" weight="light">
            Currently, you have no saved payment info. Please provide a valid credit card to process your orders.
          </RpiText>
        )}

        {hasPaymentMethods && (
          <>
            <RpiText type="p3" weight="light">
              Saved payment methods
            </RpiText>
            <Flex.Row fullWidth sx={{ flexWrap: "wrap", columnGap: 100, rowGap: 16 }}>
              {paymentMethods!.map((paymentMethod) => (
                <PaymentMethod
                  onRefetch={() => {
                    void refetch();
                    notifications.refetch(RpiNotification.PAYMENT_METHOD);
                  }}
                  key={`payment-method-${paymentMethod.token}`}
                  paymentMethod={paymentMethod}
                />
              ))}
            </Flex.Row>
          </>
        )}
      </Flex.Column>

      <RpiButton
        mt={32}
        width="fit"
        variant="brand-outline"
        size="small"
        trailingIcon
        onClick={() => setAddPaymentDrawerOpen(true)}
      >
        Add payment method
      </RpiButton>
      <PaymentMethodCreateView drawerOpen={addPaymentDrawerOpen} onClose={() => setAddPaymentDrawerOpen(false)} />
    </AppPage.Section>
  );
};
